import { ChevronDown, Check } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../DropdownMenu'
import { cn } from '../../lib/utils'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps {
  options: SelectOption[]
  placeholder?: string
  value?: string
  onChange?: (e: { target: { value: string } }) => void
  onBlur?: () => void
  disabled?: boolean
  id?: string
  name?: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

const triggerSize = {
  sm: 'h-8 px-2.5 text-xs',
  md: 'h-10 px-3 text-sm',
  lg: 'h-11 px-3.5 text-sm',
} as const

export const Select = ({
  className,
  options,
  placeholder,
  value,
  onChange,
  onBlur,
  disabled,
  id,
  name,
  size = 'md',
}: SelectProps) => {
  const selected = options.find((o) => o.value === value)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        id={id}
        name={name}
        disabled={disabled}
        onBlur={onBlur}
        className={cn(
          'inline-flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white text-gray-900 shadow-sm transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          triggerSize[size],
          className,
        )}
      >
        <span className={selected ? '' : 'text-gray-400'}>
          {selected?.label ?? placeholder ?? 'Select…'}
        </span>
        <ChevronDown className="h-4 w-4 text-gray-500" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="min-w-(--radix-dropdown-menu-trigger-width)"
      >
        {options.map((opt) => {
          const isActive = opt.value === value
          return (
            <DropdownMenuItem
              key={opt.value}
              onSelect={() => onChange?.({ target: { value: opt.value } })}
              className={cn(
                'pl-8',
                isActive && 'bg-gray-100 font-medium text-gray-900',
              )}
            >
              {isActive && (
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-blue-600 text-white">
                  <Check className="h-2.5 w-2.5" strokeWidth={3} />
                </span>
              )}
              {opt.label}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
